import { useQuery, useMutation } from '@tanstack/react-query'
import { api } from '@/services/api'

// Query keys for dictionary cache management
export const dictionaryQueryKeys = {
  stats: ['dictionary', 'stats'] as const,
  lookup: (word: string) => ['dictionary', 'lookup', word] as const,
  search: (query: string) => ['dictionary', 'search', query] as const,
  autocomplete: (prefix: string) => ['dictionary', 'autocomplete', prefix] as const,
  suggestions: (word: string) => ['dictionary', 'suggestions', word] as const,
  synonyms: (word: string) => ['dictionary', 'synonyms', word] as const,
  random: ['dictionary', 'random'] as const
}

/**
 * Hook to get dictionary statistics
 */
export function useDictionaryStats() {
  return useQuery({
    queryKey: dictionaryQueryKeys.stats,
    queryFn: () => api.getDictionaryStats(),
    staleTime: 24 * 60 * 60 * 1000 // 24 hours - stats rarely change
  })
}

/**
 * Hook to look up a word in the dictionary
 */
export function useDictionaryLookup(word: string | null) {
  return useQuery({
    queryKey: dictionaryQueryKeys.lookup(word ?? ''),
    queryFn: () => api.dictionaryLookup(word!),
    enabled: !!word && word.trim().length > 0,
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}

interface DictionarySearchOptions {
  searchMode?: 'literal' | 'position' | 'form'
  matchTypes?: Array<'exact' | 'prefix' | 'suffix' | 'contains'>
  positionOptions?: {
    skip: number[]
    repeat: number[]
    exact: number[]
    derivate: boolean
  }
  formPattern?: string
}

/**
 * Hook to search the dictionary
 */
export function useDictionarySearch(query: string, limit = 20, options?: DictionarySearchOptions) {
  return useQuery({
    queryKey: [...dictionaryQueryKeys.search(query), limit, options?.searchMode, options?.matchTypes, options?.positionOptions, options?.formPattern],
    queryFn: () => api.dictionarySearch(query, limit, options),
    enabled: query.trim().length > 0,
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}

/**
 * Hook for autocomplete suggestions
 */
export function useAutocomplete(prefix: string, limit = 10) {
  return useQuery({
    queryKey: [...dictionaryQueryKeys.autocomplete(prefix), limit],
    queryFn: () => api.autocomplete(prefix, limit),
    enabled: prefix.trim().length > 0,
    staleTime: 60 * 1000, // 1 minute
    refetchOnWindowFocus: false
  })
}

/**
 * Hook for spelling suggestions (on-demand)
 */
export function useSpellingSuggestions(word: string | null, limit = 10) {
  return useQuery({
    queryKey: [...dictionaryQueryKeys.suggestions(word ?? ''), limit],
    queryFn: () => api.getSuggestions(word!, limit),
    enabled: !!word && word.trim().length > 0,
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}

/**
 * Hook for synonyms/thesaurus
 */
export function useSynonyms(word: string | null) {
  return useQuery({
    queryKey: dictionaryQueryKeys.synonyms(word ?? ''),
    queryFn: () => api.getSynonyms(word!),
    enabled: !!word && word.trim().length > 0,
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}

/**
 * Hook to get a random word (word of the day)
 */
export function useRandomWord() {
  return useQuery({
    queryKey: dictionaryQueryKeys.random,
    queryFn: () => api.getRandomWord(),
    staleTime: 60 * 60 * 1000, // 1 hour
    refetchOnWindowFocus: false
  })
}

/**
 * Hook to perform spellcheck on an array of words
 */
export function useSpellcheck() {
  return useMutation({
    mutationFn: (words: string[]) => api.spellcheck(words)
  })
}

/**
 * Hook to perform spellcheck on text
 */
export function useSpellcheckText() {
  return useMutation({
    mutationFn: (text: string) => api.spellcheckText(text)
  })
}

/**
 * Custom hook for debounced autocomplete
 * Usage: const { suggestions, isLoading } = useDebouncedAutocomplete(inputValue)
 */
export function useDebouncedAutocomplete(prefix: string, limit = 10, _debounceMs = 300) {
  // Note: In a real implementation, you'd want to debounce this
  // For now, we rely on React Query's caching
  // _debounceMs is kept for future implementation
  return useAutocomplete(prefix, limit)
}

/**
 * Hook to look up word with manual trigger
 */
export function useLookupWord() {
  return useMutation({
    mutationFn: (word: string) => api.dictionaryLookup(word)
  })
}
